import ClientBase from "Services/API/ClientBase";

export interface LogonSuccessResponseGeneric {
   success: true,
   userId: Guid,
   firstName: string,
   fullName: string,
   showTermsOfUse?: boolean,
}

export interface LogonSuccessResponseTwoFactorRegistrationRequired {
   success: true,
   showTwoFactorRegister: true,
   lstoken: string,
}

export const isLogonSuccessResponseTwoFactorRegistrationRequired = (logonSuccessResponse: LogonSuccessResponse): logonSuccessResponse is LogonSuccessResponseTwoFactorRegistrationRequired => {
   return (logonSuccessResponse as LogonSuccessResponseTwoFactorRegistrationRequired).showTwoFactorRegister === true;
}

export interface LogonSuccessResponseTwoFactorConfirmationRequired {
   success: true,
   showTwoFactorConfirmation: true,
   phoneNumberLast4: string,
   lstoken: string,
}

export const isLogonSuccessResponseTwoFactorConfirmationRequired = (logonSuccessResponse: LogonSuccessResponse): logonSuccessResponse is LogonSuccessResponseTwoFactorConfirmationRequired => {
   return (logonSuccessResponse as LogonSuccessResponseTwoFactorConfirmationRequired).showTwoFactorConfirmation === true;
}

export type LogonSuccessResponse = LogonSuccessResponseGeneric | LogonSuccessResponseTwoFactorRegistrationRequired | LogonSuccessResponseTwoFactorConfirmationRequired;

export interface LogonErrorResponse {
   success: false,
   error: string,
   logonStatus?: LSCommon.Enums.LogonStatusEnum,
   passwordValid?: boolean
}

export type LogonResponse = LogonErrorResponse | LogonSuccessResponse;

export interface RegisterSuccessResponse {
   success: true,
   returnUrl?: string,
   userId?: Guid,
   fullName?: string,
   showTwoFactorForm?: boolean,
   data?: LSWebUI.Models.TwoFactorModel,
}

export interface RegisterErrorResponse {
   success: false,
   errors: string[],
   switchToLogin?: boolean
}

export type RegisterResponse = RegisterErrorResponse | RegisterSuccessResponse;

export type TwoFactorSuccessResponse = LogonSuccessResponse;

export interface TwoFactorErrorResponse {
   success: false,
   error: string
}

export type TwoFactorResponse = TwoFactorSuccessResponse | TwoFactorErrorResponse;

export interface TermOfUseHtmlResponse {
   termOfUseHtml: string
}

export interface ChangePasswordSuccessShowTermsOfUseResponse {
   success: true,
   showTermsOfUse: true,
   memberId: Guid,
}

export const isChangePasswordSuccessResponse = (response: ChangePasswordSuccessResponse): response is ChangePasswordSuccessShowTermsOfUseResponse => {
   return (response as ChangePasswordSuccessShowTermsOfUseResponse).showTermsOfUse === true;
}

export type ChangePasswordSuccessResponse = ChangePasswordSuccessShowTermsOfUseResponse | LogonSuccessResponse;

export interface ChangePasswordErrorResponse {
   success: false,
   errors: string[],
}

export type ChangePasswordResponse = ChangePasswordSuccessResponse | ChangePasswordErrorResponse;


export class AccountClient extends ClientBase {
   protected jsonParseReviver: ((key: string, value: any) => any) | undefined = undefined;

   constructor() {
      super();
   }

   async logon(logonRequest: Partial<LSCommon.Models.LogOnModelAnnotated>, signal?: AbortSignal | undefined): Promise<LogonResponse> {
      const url_ = "/Account/LogOn";

      const request = {...logonRequest};
      request.IsDialog = true;

      const content_ = JSON.stringify(request);

      const options_ = <RequestInit>{
         body: content_,
         method: "POST",
         signal,
         headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
            "X-Requested-With": "XMLHttpRequest", //required for .Net IsAjaxRequest() == true
         }
      };

      return fetch(url_, options_).then((_response: Response) => {
         return this.transformResult(url_, _response, (_response: Response) => _response.json() as Promise<LogonResponse>);
      });
   }

   async register(registerRequest: Partial<LSWebUI.Models.RegisterModel>, signal?: AbortSignal | undefined): Promise<RegisterResponse> {
      const url_ = "/Account/Register";

      const content_ = JSON.stringify(registerRequest);

      const options_ = <RequestInit>{
         body: content_,
         method: "POST",
         signal,
         headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
            "X-Requested-With": "XMLHttpRequest", //required for .Net IsAjaxRequest() == true
         }
      };

      return fetch(url_, options_).then((_response: Response) => {
         return this.transformResult(url_, _response, (_response: Response) => _response.json() as Promise<RegisterResponse>);
      });
   }


   async adfsProviders(signal?: AbortSignal | undefined): Promise<LSCommon.Context.AdfsProvider[]> {
      const url_ = "/account/adfsProviders";

      const options_ = <RequestInit>{
         method: "GET",
         signal,
         headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
            "X-Requested-With": "XMLHttpRequest", //required for .Net IsAjaxRequest() == true
         }
      };

      return fetch(url_, options_).then((_response: Response) => {
         return this.transformResult(url_, _response, (_response: Response) => _response.json() as Promise<LSCommon.Context.AdfsProvider[]>);
      });
   }

   async twoFactorRegister(twoFactorRegisterRequest: Partial<LSWebUI.Models.TwoFactorRegisterModel>, signal?: AbortSignal | undefined): Promise<TwoFactorResponse> {
      const url_ = "/Account/TwoFactorRegister";

      const content_ = JSON.stringify(twoFactorRegisterRequest);

      const options_ = <RequestInit>{
         body: content_,
         method: "POST",
         signal,
         headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
            "X-Requested-With": "XMLHttpRequest", //required for .Net IsAjaxRequest() == true
         }
      };

      return fetch(url_, options_).then((_response: Response) => {
         return this.transformResult(url_, _response, (_response: Response) => _response.json() as Promise<TwoFactorResponse>);
      });
   }

   async twoFactorConfirm(twoFactorConfirmRequest: Partial<LSWebUI.Models.TwoFactorConfirmModel>, signal?: AbortSignal | undefined): Promise<TwoFactorResponse> {
      const url_ = "/Account/TwoFactorConfirm";

      const content_ = JSON.stringify(twoFactorConfirmRequest);

      const options_ = <RequestInit>{
         body: content_,
         method: "POST",
         signal,
         headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
            "X-Requested-With": "XMLHttpRequest", //required for .Net IsAjaxRequest() == true
         }
      };

      return fetch(url_, options_).then((_response: Response) => {
         return this.transformResult(url_, _response, (_response: Response) => _response.json() as Promise<TwoFactorResponse>);
      });
   }

   async termsOfUseHtml(signal?: AbortSignal | undefined): Promise<TermOfUseHtmlResponse> {
      const url_ = "/Account/TermOfUseHtml";

      const options_ = <RequestInit>{
         method: "GET",
         signal,
         headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
            "X-Requested-With": "XMLHttpRequest", //required for .Net IsAjaxRequest() == true
         }
      };

      return fetch(url_, options_).then((_response: Response) => {
         return this.transformResult(url_, _response, (_response: Response) => _response.json() as Promise<TermOfUseHtmlResponse>);
      });
   }


   async forgotPassword(changePasswordRequest: LSWebUI.Models.ForgotPasswordViewModel, signal?: AbortSignal | undefined): Promise<ChangePasswordResponse> {
      const url_ = "/Account/ForgotPasswordShowMessage";

      const content_ = JSON.stringify(changePasswordRequest);

      const options_ = <RequestInit>{
         body: content_,
         method: "POST",
         signal,
         headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
            "X-Requested-With": "XMLHttpRequest", //required for .Net IsAjaxRequest() == true
         }
      };

      return fetch(url_, options_).then((_response: Response) => {
         return this.transformResult(url_, _response, (_response: Response) => _response.json() as Promise<ChangePasswordResponse>);
      });
   }

   async newScimClientToken(accountId: string, signal?: AbortSignal | undefined): Promise<string> {
      const url_ = "/Organization/NewEnterpriseToken";

      const content_ = JSON.stringify({
         accountId: accountId
      });

      const options_ = <RequestInit>{
         body: content_,
         method: "POST",
         signal,
         headers: {
            "Content-Type": "application/json",
            "Accept": "text/html",
            "X-Requested-With": "XMLHttpRequest", //required for .Net IsAjaxRequest() == true
         }
      };

      return fetch(url_, options_).then((_response: Response) => {
         return this.transformResult(url_, _response, (_response: Response) => _response.text());
      });
   }

   async newScimClientSecret(signal?: AbortSignal | undefined): Promise<string> {
      const url_ = "/Organization/NewClientSecret";

      const options_ = <RequestInit>{
         method: "POST",
         signal,
         headers: {
            "Content-Type": "application/json",
            "Accept": "text/html",
            "X-Requested-With": "XMLHttpRequest", //required for .Net IsAjaxRequest() == true
         }
      };

      return fetch(url_, options_).then((_response: Response) => {
         return this.transformResult(url_, _response, (_response: Response) => _response.text());
      });
   }
}

export default AccountClient;